import { GetTiendaNubeCustomCarriersPayload, ImportTiendaNubePayload } from '@quadminds/chameleon-ajax';
import { getStatus } from '@quadminds/chameleon-ajax';
import { ClientApi } from '@quadminds/client-apis/base';
import { axiosResolver } from '@quadminds/shared/utils';
import { API_PATHS } from './api-integrations-paths';
import { FIREBASE_URL, INTEGRATIONS_API_NAME } from './constants';
import { PROVIDERS } from './enums';
import { IntegrationsApiConfig } from './interfaces';
import {
    EditCriteriaPayload,
    PayloadEmailData,
    PostAuthPayload,
    PostCriteriaPayload,
    PostShopifyAuthPayload,
} from './payloads';
import {
    DeleteGoogleAuthResponse,
    DeleteYpfAuthResponse,
    EditCriteriaResponse,
    FetchAuthorizedProviders,
    FetchAuthorizedProvidersResponse,
    FetchMeliAuthResponse,
    FetchPickupMeliResponse,
    FetchPickupShopifyResponse,
    FetchProvidersResponse,
    FetchShopifyAuthResponse,
    FetchShopifyIntegrationUrlResponse,
    FetchTiendaNubeAuthResponse,
    FetchUserCriteriaResponse,
    FetchUserIntegrationUrlResponse,
    FetchDeliveriesTiendaNubeResponse,
    PostCriteriaResponse,
    YpfAuthResponse,
} from './responses';

/**
 * Client side integrations API.
 */
export class IntegrationsApi extends ClientApi {
    constructor(config?: IntegrationsApiConfig) {
        super({
            namespace: INTEGRATIONS_API_NAME,
            ...config,
        });
    }

    // Provider Methods
    /**
     * Get list of integration providers
     */
    public async fetchProviders(appId: string): Promise<FetchProvidersResponse> {
        return axiosResolver<FetchProvidersResponse>(() =>
            this.httpClient.get(API_PATHS.PROVIDERS.ROOT, {
                params: { appId },
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Get list of authorized providers for current user
     */
    public async fetchAuthorizedProviders(): Promise<FetchAuthorizedProvidersResponse> {
        return axiosResolver<FetchAuthorizedProvidersResponse>(() =>
            this.httpClient.get(`/${API_PATHS.PROVIDERS.ME}`, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    // Legacy method for backward compatibility
    public async fetchAuthorizedProvidersLegacy(): Promise<FetchAuthorizedProviders> {
        const url = `/${API_PATHS.PROVIDERS.ME}`;
        return axiosResolver<FetchAuthorizedProviders>(() =>
            this.httpClient.get(url, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    public async fetchUserIntegrationUrl(providerName: PROVIDERS) {
        const url = `/${providerName}/${API_PATHS.AUTH_LINK}`;

        return axiosResolver<FetchUserIntegrationUrlResponse>(() =>
            this.httpClient.get(url, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    public async fetchUserCriteria(providerName: PROVIDERS) {
        const url = `${providerName}/${API_PATHS.CRITERIA}`;

        return axiosResolver<FetchUserCriteriaResponse>(() =>
            this.httpClient.get(url, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    public async postUserCriteria({
        payload,
        providerName,
    }: {
        payload: PostCriteriaPayload;
        providerName: PROVIDERS;
    }) {
        const url = `${providerName}/${API_PATHS.CRITERIA_ZONE}`;

        return axiosResolver<PostCriteriaResponse>(() =>
            this.httpClient.post(url, payload, {
                headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
            }),
        );
    }

    public async editUserCriteria({
        payload,
        providerName,
        idCriteria,
    }: {
        payload: EditCriteriaPayload;
        providerName: string;
        idCriteria: string;
    }) {
        const url = `${providerName}/${API_PATHS.CRITERIA_ZONE}/${idCriteria}`;

        return axiosResolver<EditCriteriaResponse>(() =>
            this.httpClient.patch(url, payload, {
                headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
            }),
        );
    }

    public async fetchPickupMeli(dateFrom: string, dateTo: string) {
        const url = `/${API_PATHS.MELI.PICKUP.ME}`;

        return axiosResolver<FetchPickupMeliResponse>(() =>
            this.httpClient.get(url, {
                headers: { ...this.getAuthHeaders() },
                params: { date: dateFrom, dateTo },
            }),
        );
    }

    public async fetchDelieveriesMeli(dateFrom: string, dateTo: string, printedLabel?: boolean) {
        const url = `/${API_PATHS.MELI.DELIVERY.ME}`;

        return axiosResolver<FetchPickupMeliResponse>(() =>
            this.httpClient.get(url, {
                headers: { ...this.getAuthHeaders() },
                params: { date: dateFrom, dateTo, printedLabel },
            }),
        );
    }

    public async fetchPickupShopify(dateFrom: string, dateTo: string) {
        const url = `/${API_PATHS.SHOPIFY.PICKUP.ME}`;

        return axiosResolver<FetchPickupShopifyResponse>(() =>
            this.httpClient.get(url, {
                headers: { ...this.getAuthHeaders() },
                params: { date: dateFrom, dateTo },
            }),
        );
    }

    public async fetchDeliveriesShopify(dateFrom: string, dateTo: string) {
        const url = `/${API_PATHS.SHOPIFY.DELIVERY.ME}`;

        return axiosResolver<FetchPickupShopifyResponse>(() =>
            this.httpClient.get(url, {
                headers: { ...this.getAuthHeaders() },
                params: { date: dateFrom, dateTo },
            }),
        );
    }

    public async fetchShopifyAuth(payload: PostShopifyAuthPayload) {
        const url = `/${API_PATHS.SHOPIFY.AUTH}`;

        return axiosResolver<FetchShopifyAuthResponse>(() =>
            this.httpClient.post(url, payload, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    public async fetchMeliAuth(payload: PostAuthPayload) {
        const url = `/${API_PATHS.MELI.AUTH}`;

        return axiosResolver<FetchMeliAuthResponse>(() =>
            this.httpClient.post(url, payload, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    public async fetchTiendaNubeAuth(payload: PostAuthPayload) {
        const url = `/${API_PATHS.TIENDA_NUBE.AUTH}`;

        return axiosResolver<FetchTiendaNubeAuthResponse>(() =>
            this.httpClient.post(url, payload, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    public async fetchMeliIntegrationUrl(partialAuthId: string) {
        const url = `/${API_PATHS.MELI.AUTH_LINK}/${partialAuthId}`;

        return axiosResolver<{ url: string }>(() =>
            this.httpClient.get(url, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    public async fetchTiendaIntegrationUrl(partialAuthId: string) {
        const url = `/${API_PATHS.TIENDA_NUBE.AUTH_LINK}/${partialAuthId}`;

        return axiosResolver<{ url: string }>(() =>
            this.httpClient.get(url, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    public async fetchShopifyIntegrationUrl(shop: string, partialAuthId: string) {
        const url = `/${API_PATHS.SHOPIFY.AUTH_LINK}/${shop}/${partialAuthId}`;

        return axiosResolver<FetchShopifyIntegrationUrlResponse>(() =>
            this.httpClient.get(url, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    public async sendIntegrationEmail(payload: PayloadEmailData) {
        const url = `/sendAuthorizeProviderEmail`;

        return axiosResolver(() =>
            this.httpClient.post(url, {
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                baseURL: FIREBASE_URL,
            }),
        );
    }

    // Utilities
    getAuthHeaders() {
        let authHeader = {};
        const token = this.getCurrentToken();
        if (token) authHeader = { Authorization: `Bearer ${token}` };
        return authHeader;
    }

    getCurrentToken() {
        return localStorage.getItem('token');
    }

    public async fetchDeliveryTiendanube(params: ImportTiendaNubePayload) {
        const url = `/${API_PATHS.TIENDA_NUBE.DELIVERY.ME}`;

        return axiosResolver<FetchDeliveriesTiendaNubeResponse>(() => {
            const { status, carriers, ...rest } = params;
            return this.httpClient.get(url, {
                headers: { ...this.getAuthHeaders() },
                params: { ...rest, ...getStatus(status), carriers: JSON.stringify(carriers) },
            });
        });
    }

    public async fetchCarriersMeTiendanube(params: GetTiendaNubeCustomCarriersPayload) {
        const url = `/${API_PATHS.TIENDA_NUBE.CARRIERS.ME}`;

        return axiosResolver<{ id: string; name: string }[]>(() =>
            this.httpClient.get(url, {
                headers: { ...this.getAuthHeaders() },
                params,
            }),
        );
    }

    // YPF Integration Methods
    /**
     * Authorize with YPF
     */
    public async authorizeWithYPF(): Promise<YpfAuthResponse> {
        return axiosResolver<YpfAuthResponse>(() =>
            this.httpClient.post(
                `/${API_PATHS.YPF.AUTH}`,
                {},
                {
                    headers: { ...this.getAuthHeaders() },
                },
            ),
        );
    }

    /**
     * Delete YPF authorization
     */
    public async deleteYpfAuthorization(): Promise<DeleteYpfAuthResponse> {
        return axiosResolver<DeleteYpfAuthResponse>(() =>
            this.httpClient.delete(`/${API_PATHS.YPF.ME}`, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    // Google Integration Methods
    /**
     * Delete Google Drive authorization
     */
    public async deleteGoogleAuth(): Promise<DeleteGoogleAuthResponse> {
        return axiosResolver<DeleteGoogleAuthResponse>(() =>
            this.httpClient.delete(`/${API_PATHS.GOOGLE.AUTH_ME}`, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }
}
